/* eslint-disable react-hooks/exhaustive-deps */

import React, { ReactElement, useEffect, useMemo, useState } from 'react';

import { useAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { GrClear } from 'react-icons/gr';
import { HiFilter } from 'react-icons/hi';
import { useLocalStorage, useWindowSize } from 'usehooks-ts';

import { CSRLazy } from '@/lib/CSRLazy';
import { categories } from '@/lib/helpers';
import { LoadHeavyContentAtom } from '@/lib/store';
import {
  useElasticSearchSort,
  useElastiSearchAdList,
} from '@/modules/core/hooks/useElasticSearchServer';
import { SearchedListingsAction } from '@/modules/core/store/store';

import AdCard, { AdCardLayout } from './ListingItemCard/AdCard';
import { AdType } from './ListingItemCard/types';
import Pagination from './Pagination';
import Select from './Select';
import Text from './Text';

const InlistSquareBanner = dynamic(
  async () => {
    await new Promise((res) => {
      setTimeout(res, 2000);
    });
    return import('./banners/InlistSquareBanner');
  },
  {
    ssr: false,
    suspense: true,
  }
);

const InlistBanner = dynamic(
  async () => {
    await new Promise((res) => {
      setTimeout(res, 2000);
    });
    return import('./banners/InlistBanner');
  },
  {
    ssr: false,
    suspense: true,
  }
);

interface Props {
  onChangeTotalHitsCount?: (newTotalHitsCount: number) => void;
  category?: string | undefined;

  ssrSearchedListingsAction?: SearchedListingsAction;
}

const AdList = (props: Props) => {
  const [loadHeavyContent] = useAtom(LoadHeavyContentAtom);

  // const { listings, searchedListingsStore, handleLoadMore } =
  //   useMeilisearchAdList(props.ssrSearchedListingsAction);
  // const [currentAdCardInView] = useAtom(CurrentAdCardInViewAtom);
  const { listings, searchedListingsStore, handleLoadMore } =
    useElastiSearchAdList(props.ssrSearchedListingsAction);
  // const [SORT_OPTIONS, handleSortSelect] = useMeilisearchSort();
  const [SORT_OPTIONS, handleSortSelect] = useElasticSearchSort();

  const windowSize = useWindowSize();

  const [layout] = useLocalStorage('AdCardLayout', AdCardLayout.Fixed);

  const { pathname } = useRouter();
  const [currentPage, setCurrentPage] = useState(1);
  const router = useRouter();
  const [category, setCategory] = useState('');
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    router.push({
      pathname,
      query: { ...router.query, page: pageNumber },
    });
    searchedListingsStore.offset = pageNumber * searchedListingsStore.limit;
    searchedListingsStore.elasticsearchFilterList.categoryId = category;
    handleLoadMore();
  };
  useEffect(() => {
    if (router.query.category !== undefined) {
      const cid = categories.find((c) => c.name === router.query.category);
      if (cid?.id !== undefined) {
        setCategory(cid.id);
      }
    }

    const initialPage = parseInt(router.query.page as string, 10) || 1;
    setCurrentPage(initialPage);
    searchedListingsStore.offset =
      (initialPage - 1) * searchedListingsStore.limit;
    handleLoadMore();

    router.push({
      pathname,
      query: { ...router.query, page: initialPage },
    });
  }, [router.query.category, router.query.page]);

  useEffect(() => {
    if (props.onChangeTotalHitsCount !== undefined) {
      props.onChangeTotalHitsCount(searchedListingsStore.totalHitsCount);
    }
  }, [searchedListingsStore.totalHitsCount]);

  const splittedListings = useMemo(() => {
    const renderedListings: ReactElement[] = [];
    let bannersIndex = 1;
    listings.forEach((adCardData) => {
      const res = (
        <AdCard
          key={`listing-${adCardData.id}`}
          adType={AdType.Normal}
          adCardData={adCardData}
          className="cursor-pointer"
          layout={layout}
          index={renderedListings.length}
        />
      );
      renderedListings.push(res);

      if (bannersIndex % 13 === 0) {
        renderedListings.push(
          <CSRLazy
            key={`ad-${bannersIndex}`}
            fallback={
              <div className="relative mb-6 h-[350px] w-[210px] aspect-grid border border-gray-300 bg-slate-100 rounded-md"></div>
            }
          >
            {loadHeavyContent ? (
              <InlistBanner category={props?.category} />
            ) : (
              <div className="relative mb-6 h-[350px] w-[210px] aspect-grid border border-gray-300 bg-slate-100 rounded-md"></div>
            )}
          </CSRLazy>
        );
      }
      bannersIndex += 1;
    });

    let resListings;
    if (windowSize.width <= 640) {
      const chunkSize = 8;
      resListings = [
        ...Array(Math.ceil(renderedListings.length / chunkSize)),
      ].map((_) => renderedListings.splice(0, chunkSize));
    } else {
      resListings = [renderedListings];
    }

    return resListings;
  }, [
    listings,
    // currentAdCardInView?.index,
    windowSize.width,
  ]);

  return (
    <div className="relative -z-40">
      <div className="flex items-center justify-center md:justify-end  gap-x-2 my-3">
        {pathname !== '/' && (
          <Select
            className="select-sm w-full h-[40px] pl-10 max-w-full max-h-full select rounded-md focus:outline-none border-1 border-gray-300 bg-gray-200/70 text-xs !text-gray-600 hover:!text-gray-700 !font-bold"
            displayLabel="Trier par"
            options={SORT_OPTIONS}
            icon={<HiFilter size={20} className="text-gray-600" />}
            onChange={(e) => handleSortSelect(e.target.value)}
          />
        )}
      </div>

      {listings.length === 0 && !searchedListingsStore.isDefaultListings ? (
        <div className="flex flex-col items-center w-1/2 mx-auto gap-y-12 my-12">
          <div className="alert alert-warning shadow-lg">
            <div>
              <GrClear />
              <Text
                text="Pas de résultats pour votre recherche"
                className="font-arabic"
              />
            </div>
          </div>

          <img
            src="/media/undraw_not_found_-60-pq.svg"
            alt="No results found for your search on tayara"
          />
        </div>
      ) : (
        splittedListings.map((listingsSlice, listingsIndex) => (
          <div key={`listings-${listingsIndex}`}>
            <div
              className={`flex flex-wrap justify-center lg:justify-between xl:justify-center mt-10 mb-4  gap-y-5 gap-x-4  lg:gap-x-2  xl:gap-x-6 `}
            >
              {listingsSlice}
              {(!searchedListingsStore.totalHitsCount ||
                listings.length < searchedListingsStore.totalHitsCount) &&
              listingsIndex === splittedListings.length - 1 &&
              pathname !== '/'
                ? Array(Math.floor(windowSize.width / 90))
                    .fill(0)
                    .map((_e, _i) => <></>)
                : null}
            </div>
            {windowSize.width <= 640 ? (
              loadHeavyContent ? (
                <InlistSquareBanner />
              ) : (
                <div className="hidden lg:block mx-auto mt-2 mb-2 h-[322px] w-[322px] bg-neutral-100"></div>
              )
            ) : null}
          </div>
        ))
      )}
      {pathname !== '/' && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(searchedListingsStore.totalHitsCount)}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};
export default AdList;
